import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import { CONTROL_SIZES, SIZES } from '../utils/constant';
import { DYNAMIC_COLORS } from '../utils/dynamicColors';
import { COLOR_VARIANTS } from './colorVariants';
import { useConfig } from '../ConfigProvider';
import { useForm } from '../Form/context';
import { useInputGroup } from '../InputGroup/context';
import PropTypes from 'prop-types';
import Spinner from '../Spinner';
import useColorLevel from '../hooks/useColorLevel';

const parseColor = (color, themeColor, primaryColorLevel) => {
	const [base, level] = color.split('-');
	const buttonColor = base || themeColor;

	if (import.meta.env.DEV && !DYNAMIC_COLORS.includes(buttonColor)) {
		console.error(
			`Button: a cor "${buttonColor}" não está em src/components/ui/utils/dynamicColors.js e não vai render. `
			+ 'O Tailwind só gera as classes de um par escrito como literal "cor-nível" (ex.: "red-500") — ver tailwind.config.js.'
		);
	}

	return {
		buttonColor,
		buttonColorLevel: level || primaryColorLevel,
	};
};

const SIZE_CLASSES = {
	[SIZES.LG]: { height: 'h-12', icon: 'w-12 text-2xl', text: 'px-8 py-2 text-base' },
	[SIZES.MD]: { height: 'h-10', icon: 'w-10 text-xl', text: 'px-8 py-2' },
	[SIZES.SM]: { height: 'h-8', icon: 'w-8 text-lg', text: 'px-3 py-2 text-sm' },
	[SIZES.XS]: { height: 'h-6', icon: 'w-6 text-base', text: 'px-3 py-1 text-xs' },
};

if (import.meta.env.DEV) {
	Object.entries(CONTROL_SIZES).forEach(([size, value]) => {
		if (SIZE_CLASSES[size]?.height !== `h-${value}`) {
			console.error(`Button: CONTROL_SIZES.${size} é ${value}, mas SIZE_CLASSES.${size} usa ${SIZE_CLASSES[size]?.height}`);
		}
	});
}

const getSizeClass = (size, iconOnly) => {
	const spec = SIZE_CLASSES[size] ?? SIZE_CLASSES[SIZES.MD];

	return classNames(spec.height, iconOnly ? spec.icon : spec.text);
};

const SHAPES = ['round', 'circle', 'none'];

const VARIANTS = Object.keys(COLOR_VARIANTS);

const getColorClass = (variant, colorArgs, isDisabled) =>
	(COLOR_VARIANTS[variant] ?? COLOR_VARIANTS.default)(colorArgs, isDisabled);

const ButtonContent = ({ loading, icon, iconPosition, children }) => {
	const mark = loading
		? <Spinner enableTheme={false} aria-hidden="true" />
		: icon && <span className="text-lg">{icon}</span>;

	if (!mark) return children;
	if (children === undefined || children === null || children === false) return loading ? mark : icon;

	return <span className="flex items-center justify-center gap-1">
		{iconPosition === 'end' ? <>{children}{mark}</> : <>{mark}{children}</>}
	</span>;
};

const Button = forwardRef((props, ref) => {
	const {
		children,
		to = '',
		size,
		color = '',
		shape = 'round',
		variant = 'default',
		block = false,
		icon,
		iconPosition = 'start',
		className,
		disabled = false,
		loading = false,
		active = false,
		type,
		onClick,
		...rest
	} = props;

	const { themeColor, controlSize, primaryColorLevel } = useConfig();
	const formControlSize = useForm()?.size;
	const inputGroupSize = useInputGroup()?.size;

	const buttonSize = size || inputGroupSize || formControlSize || controlSize;
	const { buttonColor, buttonColorLevel } = parseColor(color, themeColor, primaryColorLevel);
	const [increaseLevel, decreaseLevel] = useColorLevel(buttonColorLevel);

	const isDisabled = disabled || loading;
	const iconOnly = Boolean(icon) && (children === undefined || children === null || children === false);

	const classes = classNames(
		'button',
		'inline-flex items-center justify-center transition-colors duration-200',
		getColorClass(variant, {
			color: buttonColor,
			level: buttonColorLevel,
			active,
			increaseLevel,
			decreaseLevel,
		}, isDisabled),
		`radius-${SHAPES.includes(shape) ? shape : 'round'}`,
		getSizeClass(buttonSize, iconOnly),
		block && 'w-full',
		className,
	);

	const handleClick = (e) => {
		if (isDisabled) {
			e.preventDefault();
			return;
		}
		onClick?.(e);
	};

	const state = {
		'aria-disabled': isDisabled || undefined,
		'aria-busy': loading || undefined,
	};

	const content = <ButtonContent loading={loading} icon={icon} iconPosition={iconPosition}>{children}</ButtonContent>;

	return to
		? <Link to={to} ref={ref} className={classes} onClick={handleClick} {...state} {...rest}>{content}</Link>
		: <button ref={ref} type={type ?? 'button'} className={classes} onClick={handleClick} {...state} {...rest}>{content}</button>;
});

Button.displayName = 'Button';

Button.propTypes = {
	color: PropTypes.string,
	variant: PropTypes.oneOf(VARIANTS),
	size: PropTypes.oneOf(Object.values(SIZES)),
	shape: PropTypes.oneOf(SHAPES),
	to: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
	icon: PropTypes.node,
	iconPosition: PropTypes.oneOf(['start', 'end']),
	block: PropTypes.bool,
	disabled: PropTypes.bool,
	loading: PropTypes.bool,
	active: PropTypes.bool,
	type: PropTypes.oneOf(['button', 'submit', 'reset']),
	onClick: PropTypes.func,
	className: PropTypes.string,
};

export default Button;
